// Interactive mini-map under the FinalGuessFooter using react-leaflet.
//
// Leaflet runs entirely inside the Tauri webview — no external
// navigation possible, so the user can zoom and pan without any
// risk of a click leaking the result URL into their default
// browser (which on this side-by-side ultrawide setup is the same
// browser GeoGuessr lives in).
//
// Tiles come from OpenStreetMap's tile servers (img-src https: in
// the Tauri CSP already covers them — no CSP change needed). Marker
// is a CircleMarker rather than the default Leaflet image marker
// so we don't have to bundle marker icon PNGs through Vite or
// widen img-src for unpkg/CDN paths.

import { CircleMarker, MapContainer, TileLayer, useMap } from "react-leaflet";
import { useEffect } from "react";
import "leaflet/dist/leaflet.css";

interface MiniMapProps {
  lat: number;
  lng: number;
}

/// Leaflet's MapContainer freezes its initial center/zoom and
/// doesn't re-center when props change — by design (it treats
/// the Map instance as a long-lived imperative object). We want
/// the opposite: when a new round produces a new final guess,
/// the map should fly to the new pin. This child component grabs
/// the map instance and calls `setView` whenever the coords change.
function ReCenter({ lat, lng }: MiniMapProps) {
  const map = useMap();
  useEffect(() => {
    map.setView([lat, lng], map.getZoom() ?? 6, {
      animate: true,
      duration: 0.6,
    });
  }, [map, lat, lng]);
  return null;
}

export function MiniMap({ lat, lng }: MiniMapProps) {
  // Defensive: bail on the (0,0) sentinel so a "no inference"
  // guess doesn't drop a pin in the Gulf of Guinea. Same guard
  // as the static-iframe predecessor.
  if (lat === 0 && lng === 0) {
    return null;
  }
  return (
    <div
      data-testid="mini-map"
      className="rounded overflow-hidden border border-white/10"
      style={{ height: 180 }}
    >
      <MapContainer
        center={[lat, lng]}
        zoom={6}
        scrollWheelZoom
        // Attribution stays visible (~14px bottom-right corner) to
        // satisfy OpenStreetMap's tile-usage attribution policy.
        // The card is 180px tall — 14px of attribution is an
        // acceptable cost for keeping our use of the free tile
        // service in good standing.
        style={{ width: "100%", height: "100%" }}
      >
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
        />
        <CircleMarker
          center={[lat, lng]}
          radius={8}
          pathOptions={{
            color: "#ef4444",
            fillColor: "#ef4444",
            fillOpacity: 0.7,
            weight: 2,
          }}
        />
        <ReCenter lat={lat} lng={lng} />
      </MapContainer>
    </div>
  );
}
